import {Typography, useTranslation} from '@hconnect/uikit'
import DeleteIcon from '@mui/icons-material/Delete'
import {Box, IconButton, TextField} from '@mui/material'
import {Controller, useFormContext} from 'react-hook-form'

import {
  CustomerTier,
  CustomerTierListItemProps,
  CustomerTierSettings
} from '../types/CustomerTierSettings'

import {useStyles} from './CustomerTierListItem.styles'

export const CustomerTierListItem = ({index, onDelete, totalTiers}: CustomerTierListItemProps) => {
  const {t} = useTranslation()
  const {control, getValues} = useFormContext<CustomerTierSettings>()
  const {classes} = useStyles()

  const handleDelete = () => {
    if (totalTiers > 1) {
      onDelete(index)
    }
  }
  const getFieldName = <K extends keyof CustomerTier>(fieldName: K): `tiers.${number}.${K}` => {
    return `tiers.${index}.${fieldName}`
  }

  const isLastRemainingTier = totalTiers === 1

  return (
    <Box sx={{display: 'flex', gap: 2, alignItems: 'center', mb: 2}}>
      <Box sx={{flex: 1}}>
        <Controller
          name={getFieldName('name')}
          control={control}
          rules={{
            required: t(
              'configurations.hconnect.customerTierSettings.configurationDetails.requiredField',
              {
                fieldName: t(
                  'configurations.hconnect.customerTierSettings.configurationDetails.tierNameLabel'
                )
              }
            ),
            maxLength: 1,
            validate: {
              isUnique: (currentNameValue) => {
                const formValues = getValues()
                const allTiers = formValues.tiers || []
                const isDuplicate = allTiers.some((otherTier, otherIndex) => {
                  return otherIndex !== index && otherTier.name === currentNameValue
                })

                return (
                  !isDuplicate ||
                  t(
                    'configurations.hconnect.customerTierSettings.configurationDetails.tierNameNotUnique'
                  )
                )
              }
            }
          }}
          render={({field: controllerField, fieldState}) => (
            <Box>
              <TextField
                {...controllerField}
                fullWidth
                label={t(
                  'configurations.hconnect.customerTierSettings.configurationDetails.tierNameLabel'
                )}
                type="text"
                variant="standard"
                className={`${classes.textField} ${fieldState.error ? classes.errorField : ''}`}
                InputProps={{disableUnderline: true}}
                InputLabelProps={{className: classes.inputLabel, shrink: true}}
                error={!!fieldState.error}
                data-test-id={`customer-tier-name-input-${index}`}
              />
              {fieldState.error && (
                <Typography
                  className={classes.errorText}
                  data-test-id={`customer-tier-settings-validation-${getFieldName(
                    'name'
                  )}-${index}`}
                >
                  {fieldState.error.message}
                </Typography>
              )}
            </Box>
          )}
        />
      </Box>
      <Box sx={{flex: 1}}>
        <Controller
          name={getFieldName('percentage')}
          control={control}
          rules={{
            required: t(
              'configurations.hconnect.customerTierSettings.configurationDetails.requiredField',
              {
                fieldName: t(
                  'configurations.hconnect.customerTierSettings.configurationDetails.tierPercentageLabel'
                )
              }
            ),
            min: {
              value: 1,
              message: t(
                'configurations.hconnect.customerTierSettings.configurationDetails.tierPercentageNotValid'
              )
            },
            max: {
              value: 99,
              message: t(
                'configurations.hconnect.customerTierSettings.configurationDetails.tierPercentageNotValid'
              )
            }
          }}
          render={({field: controllerField, fieldState}) => (
            <Box>
              <TextField
                {...controllerField}
                fullWidth
                label={t(
                  'configurations.hconnect.customerTierSettings.configurationDetails.tierPercentageLabel'
                )}
                type="number"
                variant="standard"
                className={`${classes.textField} ${fieldState.error ? classes.errorField : ''}`}
                InputProps={{disableUnderline: true}}
                InputLabelProps={{className: classes.inputLabel, shrink: true}}
                error={!!fieldState.error}
                data-test-id={`customer-tier-percentage-input-${index}`}
              />
              {fieldState.error && (
                <Typography
                  className={classes.errorText}
                  data-test-id={`customer-tier-settings-validation-${getFieldName(
                    'percentage'
                  )}-${index}`}
                >
                  {fieldState.error.message}
                </Typography>
              )}
            </Box>
          )}
        />
      </Box>
      <IconButton
        onClick={handleDelete}
        color="error"
        disabled={isLastRemainingTier}
        data-test-id={`customer-tier-delete-button-${index}`}
      >
        <DeleteIcon />
      </IconButton>
    </Box>
  )
}
